<template>
  <div class="faste-tillaeg text-dark">
    <h2 class="marg-1-bottom capitalize">Faste tillæg</h2>

    <div class="faste-tillaeg__header">
      <div class="faste-tillaeg__header__left">
        <Tabs
          :tabs="viewTabs"
          :selected="viewTab"
          @select="onSelectViewTab">
        </Tabs>
        <DoFiltersFilter
          :title="'Personer'"
          :tags="personNames"
          :selectedTags="selectedNames"
          :numberOfSelectedTagsToShow="1"
          :placeholder="'Vælg personer'"
          @select="toggleName"
          @clear="selectedNames = []">
        </DoFiltersFilter>
        <DoFiltersFilter
          :title="'Grupper'"
          :tags="tags"
          :selectedTags="selectedTags"
          :numberOfSelectedTagsToShow="1"
          :placeholder="'Vælg grupper'"
          @select="toggleTag"
          @clear="selectedTags = []">
        </DoFiltersFilter>
        <DoFiltersFilter
          v-if="employmentTitles.length > 1"
          :title="'Ansættelser'"
          :tags="employmentTitles"
          :selectedTags="selectedEmploymentTitles"
          :numberOfSelectedTagsToShow="1"
          :placeholder="'Vælg stilling'"
          @select="toggleEmploymentTitle"
          @clear="selectedEmploymentTitles = []">
        </DoFiltersFilter>
        <DoFiltersFilter
          v-if="organisations.length > 1"
          :title="'Organisationer'"
          :tags="organisations"
          :selectedTags="selectedOrganisations"
          :numberOfSelectedTagsToShow="1"
          :placeholder="'Vælg organisation'"
          @select="toggleOrganisation"
          @clear="selectedOrganisations = []">
        </DoFiltersFilter>
        <span class="faste-tillaeg__count" v-if="viewTab === 'opus'">
          Viser {{ filteredRows.length }} af {{ allRows.length }} ansættelser
        </span>
      </div>
    </div>

    <div class="faste-tillaeg__toolbar" v-if="viewTab === 'opus'">
      <span class="faste-tillaeg__toolbar__wrap"
            :content="selectedEmploymentIds.length === 0 ? 'Vælg personer først' : ''"
            v-tippy="{
              arrow: true,
              placement: 'top',
              animateFill: false,
              animation: 'shift-away',
              boundary: 'window'
            }">
        <button class="do-bttn do-bttn--submit"
                :class="{'do-bttn--submit--disabled': selectedEmploymentIds.length === 0}"
                :disabled="selectedEmploymentIds.length === 0"
                @click="openBulkCreate">
          Opret faste tillæg
        </button>
      </span>
      <DDropdown :isBottomStart="true" :isShowDots="false" :propOffset="[0, 8]">
        <button slot="title" class="do-bttn" style="font-size: 11px;">
          Beregn tillæg automatisk <span class="icon-arrow-down5"></span>
        </button>
        <ul slot="list" class="beregn-menu">
          <li v-for="option in visibleBeregnOptions"
              :key="option.id"
              @click="selectBeregnOption(option)">
            {{ option.label }}
          </li>
        </ul>
      </DDropdown>
      <button class="do-bttn" v-if="selectedEmploymentIds.length > 0" @click="cancelBatchMode">Annuller valg</button>
      <span class="faste-tillaeg__count" v-if="selectedEmploymentIds.length > 0">
        {{ selectedEmploymentIds.length }} valgt
      </span>
    </div>

    <div class="faste-tillaeg__body">
      <Loader v-if="isLoadingEmployments"></Loader>

      <!-- Tab: Alle i OPUS -->
      <table v-else-if="viewTab === 'opus'" class="do-table">
        <thead>
        <tr>
          <th class="td-check do-table__toggle-all" @click="toggleAllSelected">
            <div class="do-checkbox" :class="{'do-checkbox--selected': isAllFilteredSelected}"></div>
          </th>
          <th>NAVN</th>
          <th>ANSÆTTELSE</th>
          <th>ORGANISATION</th>
          <th class="th-actions"></th>
        </tr>
        </thead>
        <tbody v-if="filteredRows.length === 0">
        <tr>
          <td :colspan="5" class="italic">Ingen ansættelser fundet</td>
        </tr>
        </tbody>
        <tbody v-for="group in groupedRows" :key="group.userId" class="user-group">
        <tr v-for="row in group.rows"
            :key="row.id"
            class="fast-tillaeg-row"
            :class="{'is-selected': isRowSelected(row)}"
            @click="onRowClick(row)">
          <td class="td-check" @click.stop="onCheckboxClick(row)">
            <div class="do-checkbox" :class="{'do-checkbox--selected': isRowSelected(row)}"></div>
          </td>
          <td class="bold">{{ row.userName }}</td>
          <td class="td-organisation">
            {{ row.employmentNumber }} - {{ row.employmentTitle }}
          </td>
          <td class="td-organisation">{{ row.organisation }}</td>
          <td class="td-actions">
            <button
              v-if="!isBatchMode"
              type="button"
              class="do-bttn do-bttn--small row-action-btn"
              @click.stop="openSingleCreate(row)"
            >Opret fast tillæg</button>
          </td>
        </tr>
        </tbody>
      </table>

      <!-- Tab: Udbakke / Afsendte -->
      <template v-else>
        <div class="faste-tillaeg__outbox-bar">
          <span class="faste-tillaeg__outbox-info">
            <template v-if="isLoadingOutbox">Indlæser…</template>
            <template v-else-if="viewTab === 'sent'">
              Afsendte og godkendte tillæg i OPUS. Klik på "Opdater"-knappen for at se den aktuelle status.
            </template>
            <template v-else>
              Tillæg i udbakken behandles løbende af OPUS. Klik på "Opdater"-knappen for at se den aktuelle status.
            </template>
          </span>
          <button class="do-bttn do-bttn--small" :disabled="isLoadingOutbox" @click="loadOutbox">
            Opdater
          </button>
          <button class="do-bttn do-bttn--small"
                  :class="{'do-bttn--loading': isDownloadingOutbox}"
                  :disabled="isLoadingOutbox || isDownloadingOutbox || currentOutboxRows.length === 0"
                  @click="downloadOutboxExcel">
            Download til excel
          </button>
        </div>
        <Loader v-if="isLoadingOutbox"></Loader>
        <table v-else class="do-table do-table--striped">
          <thead>
          <tr>
            <th class="sortable td-created" @click="sortOutbox('created_at')">OPRETTET <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable" @click="sortOutbox('user')">NAVN <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable" @click="sortOutbox('employment')">ANSÆTTELSE <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable td-loenkode" @click="sortOutbox('loenkode')">LØNKODE <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable td-units" @click="sortOutbox('antal')">ENHEDER <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable td-units" @click="sortOutbox('beloeb')">BELØB <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable td-date" @click="sortOutbox('fra')">FRA <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable td-date" @click="sortOutbox('til')">TIL <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable" @click="sortOutbox('operation')">HANDLING <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable" @click="sortOutbox('created_by')">OPRETTET AF <span class="icon-menu-open sort-icon"></span></th>
            <th class="sortable td-status" @click="sortOutbox('state')">STATUS <span class="icon-menu-open sort-icon"></span></th>
            <th class="th-actions" v-if="viewTab === 'outbox'"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="currentOutboxRows.length === 0">
            <td :colspan="viewTab === 'outbox' ? 12 : 11" class="italic">
              {{ viewTab === 'sent' ? 'Ingen afsendte ændringer' : 'Ingen afventende ændringer i udbakken' }}
            </td>
          </tr>
          <tr v-for="row in currentOutboxRows"
              :key="row.vRowKey"
              class="outbox-row">
            <td class="td-created">{{ row.vCreatedAt }}</td>
            <td class="bold">{{ row.vUserName }}</td>
            <td class="td-organisation">{{ row.vEmploymentLabel }}</td>
            <td class="td-loenkode">{{ row.vLoenart }}</td>
            <td class="td-units">{{ formatDecimal(row.antal) }}</td>
            <td class="td-units">{{ formatDecimal(row.beloeb) }}</td>
            <td class="td-date">{{ row.vFraDato }}</td>
            <td class="td-date">{{ row.vTilDato }}</td>
            <td>{{ operationLabel(row.operation) }}</td>
            <td>
              <template v-if="row.vCreatedByName">{{ row.vCreatedByName }}</template>
              <span v-else-if="row.vCreatedById != null"
                    class="italic"
                    :content="'Bruger-id: ' + row.vCreatedById"
                    v-tippy="{
                      arrow: true,
                      placement: 'top',
                      animateFill: false,
                      animation: 'shift-away',
                      boundary: 'window'
                    }">
                Tidligere ansat
              </span>
              <template v-else>—</template>
            </td>
            <td class="td-status">
              <span class="do-pill do-pill--small" :class="statePillClass(row)">
                {{ stateLabel(row) }}
              </span>
              <div v-for="(msg, i) in resultMessages(row)" :key="i"
                   class="outbox-msg" :class="{'outbox-msg--error': msg.type === 'E'}">
                {{ msg.code }}: {{ msg.text }}
              </div>
            </td>
            <td class="td-actions" v-if="viewTab === 'outbox'">
              <button v-if="row.state === 'failed'"
                      class="do-bttn do-bttn--small do-bttn--danger"
                      :disabled="deletingIds.includes(row.id)"
                      @click="deleteFailedRow(row)">
                Slet
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </template>
    </div>

    <FasteTillaegModal
      v-if="activeRow"
      :schoolId="schoolId"
      :employmentId="activeRow.employmentId"
      :employment="activeRow.employment"
      :userName="activeRow.userName"
      @close="activeRow = null">
    </FasteTillaegModal>

    <CreateFastTillaeg
      v-if="createEmploymentIds.length > 0"
      :schoolId="schoolId"
      :employmentIds="createEmploymentIds"
      :subtitle="createSubtitle"
      @created="onBulkCreated"
      @close="closeCreate">
    </CreateFastTillaeg>

    <TidsbegraensetUvModal
      v-if="isShowTidsbegraenset"
      @created="isShowTidsbegraenset = false"
      @close="isShowTidsbegraenset = false">
    </TidsbegraensetUvModal>

    <DModal
      v-if="rowToDelete"
      :show-close="false"
      :isBgOverlay="true"
      :isCloseOnBgClick="false"
      @close="rowToDelete = null"
      :appendToBody="true"
      style="z-index: 12312312312313"
      :placeInTop="true">
      <div class="d-modal d-modal--small" slot="insertComponent">
        <div class="d-modal__header">
          <h2>Slet linje</h2>
          <div class="font-large icon__bg-circle d-modal__header__close" @click="rowToDelete = null">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="d-modal__content">
          <p>Er du sikker på, at du vil slette denne fejlede linje fra udbakken? Handlingen kan ikke fortrydes.</p>
        </div>
        <div class="d-modal__buttons">
          <div class="d-modal__buttons__child">
            <button class="do-bttn" @click="rowToDelete = null">Annuller</button>
            <button class="do-bttn do-bttn--submit do-bttn--danger"
                    :class="{'do-bttn--submit--loading': deletingIds.includes(rowToDelete.id)}"
                    :disabled="deletingIds.includes(rowToDelete.id)"
                    @click="confirmDeleteFailedRow">
              Slet linje
            </button>
          </div>
        </div>
      </div>
    </DModal>

    <DModal
      v-if="isShowBeregnModal"
      :show-close="false"
      :isBgOverlay="true"
      :isCloseOnBgClick="false"
      @close="closeBeregnModal"
      :appendToBody="true"
      style="z-index: 12312312312313"
      :placeInTop="true">
      <div class="beregn-modal text-dark" slot="insertComponent">
        <div class="beregn-modal__header">
          <h2>{{ beregnModalTitle }}</h2>
          <div class="font-large icon__bg-circle beregn-modal__close" @click="closeBeregnModal">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="beregn-modal__content">
          <Supplements
            :isInOpusModal="true"
            :beregningType="beregnType"
            :sendToOpusTrigger="sendToOpusTrigger"
            @approveState="opusApproveState = $event">
          </Supplements>
        </div>
        <div class="beregn-modal__footer" v-if="opusApproveState.canApprove">
          <span class="send-to-opus-wrap"
                :content="sendToOpusTooltip"
                v-tippy="{
                  arrow: true,
                  placement: 'top',
                  animateFill: false,
                  animation: 'shift-away',
                  boundary: 'window',
                  onShow: () => sendToOpusTooltip !== ''
                }">
            <button class="do-bttn do-bttn--submit"
                    :class="{'do-bttn--submit--loading': opusApproveState.isApproving, 'do-bttn--submit--disabled': opusApproveState.count === 0 || opusApproveState.missingEmployment}"
                    :disabled="opusApproveState.isApproving || opusApproveState.count === 0 || opusApproveState.missingEmployment"
                    @click="triggerSendToOpus">
              Send til OPUS
            </button>
          </span>
        </div>
      </div>
    </DModal>
  </div>
</template>

<script>
import Loader from '../../common/Loader.vue'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
import Tabs from '../../common/Tabs.vue'
import FasteTillaegModal from './FasteTillaegModal.vue'
import CreateFastTillaeg from './CreateFastTillaeg.vue'
import DModal from '../../common/DModal.vue'
import DDropdown from '../../common/DDropdown.vue'
import Supplements from '../../Supplements/Supplements.vue'
import TidsbegraensetUvModal from '../../Supplements/TidsbegraensetUvModal.vue'
import docendo from '../../../docendo'
import moment from 'moment'
import eventbus from '../../../eventbus'

let XLSX = null
export default {
  name: 'FasteTillaeg',
  components: {Loader, DoFiltersFilter, Tabs, FasteTillaegModal, CreateFastTillaeg, DModal, DDropdown, Supplements, TidsbegraensetUvModal},
  data () {
    return {
      selectedNames: [],
      selectedTags: [],
      selectedEmploymentTitles: [],
      selectedOrganisations: [],
      selectedEmploymentIds: [],
      isBatchMode: false,
      activeRow: null,
      createEmploymentIds: [],
      createSubtitle: '',
      isShowTidsbegraenset: false,
      isShowBeregnModal: false,
      beregnType: null,
      sendToOpusTrigger: 0,
      opusApproveState: {count: 0, isApproving: false, canApprove: false, missingEmployment: false},
      beregnOptions: [
        {id: 'uv_over_graense', label: 'Undervisningstillæg – timer over grænse (§5 stk. 2)'},
        {id: 'spec_skole_uv', label: 'Specialundervisning (§ 5, stk. 4)'},
        {id: 'dsa_tale_hoere', label: 'DSA og tale/høre-undervisning (§5 stk. 9 og 10)'},
        {id: 'paedagog_eneansvar', label: 'Pædagog med eneansvar'}
      ],
      viewTab: 'opus',
      outboxRows: [],
      isLoadingOutbox: false,
      outboxSortKey: 'created_at',
      outboxSortDir: 'desc',
      hasLoadedOutbox: false,
      deletingIds: [],
      rowToDelete: null,
      isDownloadingOutbox: false
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    storageKey () {
      return 'fasteTillaegFilters:' + (this.schoolId || 'default')
    },
    filtersSnapshot () {
      return JSON.stringify({
        viewTab: this.viewTab,
        selectedNames: this.selectedNames,
        selectedTags: this.selectedTags,
        selectedEmploymentTitles: this.selectedEmploymentTitles,
        selectedOrganisations: this.selectedOrganisations
      })
    },
    beregnModalTitle () {
      const option = this.beregnOptions.find(o => o.id === this.beregnType)
      return option ? option.label : 'Beregn tillæg automatisk'
    },
    sendToOpusTooltip () {
      if (this.opusApproveState.count === 0) return 'Opret lønlinjer først'
      if (this.opusApproveState.missingEmployment) return 'Vælg ansættelse på alle linjer'
      return ''
    },
    isLoadingEmployments () {
      return this.$store.state.employments && this.$store.state.employments.isLoadingEmploymentsTypesForSchool
    },
    users () {
      return this.$store.getters.usersSortedOnName || []
    },
    // Opslag fra bruger-id til bruger (til "Oprettet af").
    userById () {
      const map = {}
      for (let i = 0; i < this.users.length; i++) map[this.users[i].id] = this.users[i]
      return map
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    // Ét row pr. (bruger, OPUS-ansættelse) med tjenestenummer.
    allRows () {
      const out = []
      for (let i = 0; i < this.users.length; i++) {
        const user = this.users[i]
        const emps = this.employmentsInUserIdHash[user.id] || []
        for (let j = 0; j < emps.length; j++) {
          const emp = emps[j]
          const empNumber = emp && emp.employment_number ? String(emp.employment_number) : ''
          if (empNumber.trim() === '') continue
          if (emp.source && emp.source !== 'opus') continue
          // Kun ansættelser med en stillingskategori kan vælges til faste tillæg.
          if (emp.employment_template_id == null) continue
          const title = emp.source_title || emp.title || 'Ansættelse'
          out.push({
            id: user.id + '_' + emp.id,
            userId: user.id,
            userName: user.name,
            userTags: Array.isArray(user.user_tags) ? user.user_tags : [],
            employmentId: emp.id,
            employmentTitle: title,
            employmentNumber: empNumber,
            organisation: emp.organisation || '',
            employment: emp
          })
        }
      }
      return out.sort((a, b) => a.userName.localeCompare(b.userName, 'da-DK', {sensitivity: 'base'}))
    },
    personNames () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].userName) set[this.allRows[i].userName] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    tags () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        const t = this.allRows[i].userTags || []
        for (let j = 0; j < t.length; j++) {
          if (t[j]) set[t[j]] = true
        }
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    employmentTitles () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].employmentTitle) set[this.allRows[i].employmentTitle] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    organisations () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        if (this.allRows[i].organisation) set[this.allRows[i].organisation] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    filteredRows () {
      return this.allRows.filter(row => {
        if (this.selectedNames.length > 0 && this.selectedNames.indexOf(row.userName) === -1) {
          return false
        }
        if (this.selectedTags.length > 0) {
          const match = row.userTags && this.selectedTags.some(t => row.userTags.indexOf(t) > -1)
          if (!match) return false
        }
        if (this.selectedEmploymentTitles.length > 0 && this.selectedEmploymentTitles.indexOf(row.employmentTitle) === -1) {
          return false
        }
        if (this.selectedOrganisations.length > 0 && this.selectedOrganisations.indexOf(row.organisation) === -1) {
          return false
        }
        return true
      })
    },
    isAllFilteredSelected () {
      if (this.filteredRows.length === 0) return false
      return this.filteredRows.every(row => this.selectedEmploymentIds.indexOf(row.employmentId) > -1)
    },
    // Grupperer rækkerne pr. person, så baggrundsfarven banderer pr. person og ikke pr. række.
    groupedRows () {
      const groups = []
      const indexByUser = {}
      for (let i = 0; i < this.filteredRows.length; i++) {
        const row = this.filteredRows[i]
        if (indexByUser[row.userId] === undefined) {
          indexByUser[row.userId] = groups.length
          groups.push({userId: row.userId, rows: []})
        }
        groups[indexByUser[row.userId]].rows.push(row)
      }
      return groups
    },
    viewTabs () {
      return [
        {id: 'opus', title: 'Oprettelse'},
        {id: 'outbox', title: 'Udbakke'},
        {id: 'sent', title: 'Afsendte'}
      ]
    },
    salaryCodes () {
      return this.$store.getters.salaryCodesSorted || []
    },
    hasSpecialSalaryCode () {
      return this.salaryCodes.some(salaryCode => String(salaryCode.loenart) === '8411' || String(salaryCode.code) === '8411')
    },
    // Specialundervisning (§ 5, stk. 4) kan kun beregnes hvis lønarten 8411 er oprettet.
    visibleBeregnOptions () {
      return this.beregnOptions
        .filter(option => option.id !== 'spec_skole_uv' || this.hasSpecialSalaryCode)
    },
    // Lønkoder slås op på den fulde lønkode (4 cifre = lønart + løbenummer).
    salaryCodesByLoenkode () {
      const map = {}
      for (let i = 0; i < this.salaryCodes.length; i++) {
        const code = this.salaryCodes[i]
        if (code.loenart) map[String(code.loenart)] = code
      }
      return map
    },
    // Opslag fra medarbejdernummer (lønsystemets heltal) til personrække.
    employmentRowByNumber () {
      const map = {}
      const rows = this.allRows
      for (let i = 0; i < rows.length; i++) {
        const num = parseInt(rows[i].employmentNumber, 10)
        if (!isNaN(num)) map[String(num)] = rows[i]
      }
      return map
    },
    // Beriger og filtrerer alle outbox-rækker (alle states undtagen slettede) med person-/ansættelsesdata,
    // lønkode-tekst og sortering. Bruges af både "Udbakke" og "Afsendte".
    enrichedOutboxRows () {
      const rowByNumber = this.employmentRowByNumber
      const userById = this.userById
      let rows = this.outboxRows.filter(rec => rec.state !== 'deleted').map(rec => {
        const match = rowByNumber[String(rec.medarbejder_nummer)] || null
        const creator = rec.user_id != null ? (userById[rec.user_id] || null) : null
        return Object.assign({}, rec, {
          vUserName: match ? match.userName : ('Medarbejder ' + rec.medarbejder_nummer),
          vUserId: match ? match.userId : null,
          vUserTags: match ? match.userTags : [],
          vEmploymentId: match ? match.employmentId : null,
          vEmployment: match ? match.employment : null,
          vEmploymentLabel: match ? (match.employmentNumber + ' - ' + match.employmentTitle) : String(rec.medarbejder_nummer),
          vEmploymentTitle: match ? match.employmentTitle : '',
          vOrganisation: match ? match.organisation : '',
          vCreatedById: rec.user_id != null ? rec.user_id : null,
          vCreatedByName: creator ? creator.name : null
        })
      })
      rows = rows.filter(row => {
        if (this.selectedNames.length > 0 && this.selectedNames.indexOf(row.vUserName) === -1) {
          return false
        }
        if (this.selectedTags.length > 0) {
          const match = row.vUserTags && this.selectedTags.some(t => row.vUserTags.indexOf(t) > -1)
          if (!match) return false
        }
        if (this.selectedEmploymentTitles.length > 0 && this.selectedEmploymentTitles.indexOf(row.vEmploymentTitle) === -1) {
          return false
        }
        if (this.selectedOrganisations.length > 0 && this.selectedOrganisations.indexOf(row.vOrganisation) === -1) {
          return false
        }
        return true
      })
      rows = rows.map(item => {
        const loenkode = this.loenkodeForRow(item)
        const salaryCode = loenkode ? this.salaryCodesByLoenkode[loenkode] : null
        const title = (salaryCode && salaryCode.name) || ''
        const vLoenart = loenkode
          ? (title ? title + ' (' + loenkode + ')' : loenkode)
          : '—'
        return Object.assign({}, item, {
          vLoenart,
          vCreatedAt: item.created_at ? moment(item.created_at).format('DD-MM-YYYY HH:mm') : '',
          vFraDato: item.startdato ? moment(item.startdato).format('DD-MM-YYYY') : '',
          vTilDato: item.stopdato ? moment(item.stopdato).format('DD-MM-YYYY') : ''
        })
      })
      const dir = this.outboxSortDir === 'asc' ? 1 : -1
      const key = this.outboxSortKey
      return rows.sort((a, b) => {
        const av = this.outboxSortValue(a, key)
        const bv = this.outboxSortValue(b, key)
        let cmp
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv
        } else {
          cmp = String(av).localeCompare(String(bv), 'da-DK', {sensitivity: 'base'})
        }
        return cmp * dir
      })
    },
    // Udbakken: ændringer der afventer behandling eller er fejlet.
    displayedOutboxRows () {
      return this.enrichedOutboxRows.filter(row => row.state !== 'sent')
    },
    // Afsendte: ændringer der er afsendt/godkendt i OPUS.
    displayedSentRows () {
      return this.enrichedOutboxRows.filter(row => row.state === 'sent')
    },
    // Rækkerne der vises i den aktive fane (Udbakke eller Afsendte).
    currentOutboxRows () {
      return this.viewTab === 'sent' ? this.displayedSentRows : this.displayedOutboxRows
    }
  },
  methods: {
    sortOutbox (key) {
      if (this.outboxSortKey === key) {
        this.outboxSortDir = this.outboxSortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.outboxSortKey = key
        this.outboxSortDir = 'asc'
      }
    },
    outboxSortValue (row, key) {
      switch (key) {
        case 'created_at': return row.created_at ? moment(row.created_at).valueOf() : 0
        case 'user': return (row.vUserName || '').toLowerCase()
        case 'employment': return (row.vEmploymentLabel || '').toLowerCase()
        case 'loenkode': return (row.vLoenart || '').toLowerCase()
        case 'antal': return row.antal != null && row.antal !== '' ? parseFloat(String(row.antal).replace(',', '.')) : 0
        case 'beloeb': return row.beloeb != null && row.beloeb !== '' ? parseFloat(String(row.beloeb).replace(',', '.')) : 0
        case 'fra': return row.startdato ? moment(row.startdato).valueOf() : 0
        case 'til': return row.stopdato ? moment(row.stopdato).valueOf() : 0
        case 'operation': return this.operationLabel(row.operation) || ''
        case 'created_by': return (row.vCreatedByName || (row.vCreatedById != null ? 'tidligere ansat' : '')).toLowerCase()
        case 'state': return this.stateLabel(row) || ''
        default: return ''
      }
    },
    // Rekonstruerer den fulde lønkode (lønart polstret til 3 cifre + løbenummer) for en outbox-række.
    loenkodeForRow (item) {
      if (item.loenart == null) return ''
      const loenart = String(item.loenart).padStart(3, '0')
      const loebenummer = item.loebenummer != null ? String(item.loebenummer) : '0'
      return loenart + loebenummer
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx > -1) this.selectedNames.splice(idx, 1)
      else this.selectedNames.push(name)
    },
    toggleTag (tag) {
      const idx = this.selectedTags.indexOf(tag)
      if (idx > -1) this.selectedTags.splice(idx, 1)
      else this.selectedTags.push(tag)
    },
    toggleEmploymentTitle (title) {
      const idx = this.selectedEmploymentTitles.indexOf(title)
      if (idx > -1) this.selectedEmploymentTitles.splice(idx, 1)
      else this.selectedEmploymentTitles.push(title)
    },
    toggleOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) this.selectedOrganisations.splice(idx, 1)
      else this.selectedOrganisations.push(organisation)
    },
    isRowSelected (row) {
      return this.selectedEmploymentIds.indexOf(row.employmentId) > -1
    },
    toggleSelection (row) {
      const idx = this.selectedEmploymentIds.indexOf(row.employmentId)
      if (idx > -1) this.selectedEmploymentIds.splice(idx, 1)
      else this.selectedEmploymentIds.push(row.employmentId)
    },
    toggleAllSelected () {
      this.isBatchMode = true
      const visibleIds = this.filteredRows.map(row => row.employmentId)
      if (this.isAllFilteredSelected) {
        // Fravælg alle synlige
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => visibleIds.indexOf(id) === -1)
      } else {
        // Tilføj alle synlige
        const set = {}
        for (let i = 0; i < this.selectedEmploymentIds.length; i++) set[this.selectedEmploymentIds[i]] = true
        for (let i = 0; i < visibleIds.length; i++) set[visibleIds[i]] = true
        this.selectedEmploymentIds = Object.keys(set).map(id => parseInt(id, 10))
      }
    },
    cancelBatchMode () {
      this.isBatchMode = false
      this.selectedEmploymentIds = []
    },
    openBulkCreate () {
      if (this.selectedEmploymentIds.length === 0) return
      this.createEmploymentIds = this.selectedEmploymentIds.slice()
      this.createSubtitle = ''
    },
    openSingleCreate (row) {
      // Et rækkeklik (eller knappen) opretter et fast tillæg direkte på den enkelte ansættelse.
      this.createEmploymentIds = [row.employmentId]
      this.createSubtitle = row.userName + ' – ' + row.employmentNumber + ' - ' + row.employmentTitle
    },
    closeCreate () {
      this.createEmploymentIds = []
      this.createSubtitle = ''
    },
    onBulkCreated () {
      this.createEmploymentIds = []
      this.createSubtitle = ''
      this.selectedEmploymentIds = []
      this.isBatchMode = false
      // Udbakken er ændret; genindlæs så antallet på fanen er opdateret.
      this.loadOutbox()
    },
    onRowClick (row) {
      // Uden for bulk-mode opretter et rækkeklik et fast tillæg på ansættelsen; i bulk-mode toggles valget.
      if (this.isBatchMode) {
        this.toggleSelection(row)
      } else {
        this.openSingleCreate(row)
      }
    },
    onCheckboxClick (row) {
      // Klik direkte på checkboxen slår bulk-mode til og vælger ansættelsen.
      if (!this.isBatchMode) this.isBatchMode = true
      this.toggleSelection(row)
    },
    openModal (row) {
      this.activeRow = row
    },
    selectBeregnOption (option) {
      if (option.id === 'tidsbegraenset_uv') {
        this.isShowTidsbegraenset = true
        return
      }
      this.beregnType = option.id
      this.opusApproveState = {count: 0, isApproving: false, canApprove: false, missingEmployment: false}
      this.isShowBeregnModal = true
    },
    triggerSendToOpus () {
      this.sendToOpusTrigger++
    },
    closeBeregnModal () {
      this.isShowBeregnModal = false
      this.beregnType = null
      // Beregningen kan have sendt ændringer til udbakken; genindlæs så antallet/status er opdateret.
      this.loadOutbox()
    },
    deleteFailedRow (row) {
      if (row.id == null) return
      this.rowToDelete = row
    },
    confirmDeleteFailedRow () {
      const row = this.rowToDelete
      if (!row || row.id == null || this.deletingIds.includes(row.id)) return
      this.deletingIds.push(row.id)
      docendo.deleteFailedRecurringPayments(this.schoolId, [row.id], () => {
        this.deletingIds = this.deletingIds.filter(id => id !== row.id)
        // Backenden soft-sletter (state -> 'deleted'), så posten falder ud af udbakken ved genindlæsning.
        this.outboxRows = this.outboxRows.filter(r => r.id !== row.id)
        this.rowToDelete = null
        eventbus.toast = {text: 'Linjen blev slettet', success: true, position: 'bottomLeft'}
      }, failure => {
        console.log('Kunne ikke slette udbakke-linje', failure)
        this.deletingIds = this.deletingIds.filter(id => id !== row.id)
        eventbus.toast = {text: 'Kunne ikke slette linjen', success: false, position: 'bottomLeft'}
      })
    },
    onSelectViewTab (tab) {
      this.viewTab = tab
      // Genindlæs altid listen når Udbakke/Afsendte vælges, så status afspejler den aktuelle behandling i OPUS.
      if ((tab === 'outbox' || tab === 'sent') && !this.isLoadingOutbox) {
        this.loadOutbox()
      }
    },
    // Henter udbakken (ikke-afsendte ændringer) for hele skolen i ét kald.
    // Backend (read_updates) returnerer alle ansættelsers afventende ændringer på én gang.
    // Person-/ansættelsesopslag sker reaktivt i displayedOutboxRows, så antallet kan vises
    // med det samme – også før ansættelserne er hentet.
    loadOutbox () {
      if (this.isLoadingOutbox) return
      this.isLoadingOutbox = true
      this.hasLoadedOutbox = true

      docendo.listRecurringPaymentsOutbox(this.schoolId, null, success => {
        const list = Array.isArray(success)
          ? success
          : (success && Array.isArray(success.data) ? success.data : [])
        this.outboxRows = list.map((rec, i) => Object.assign({}, rec, {
          vRowKey: rec.id != null ? 'id_' + rec.id : ('idx_' + i)
        }))
        this.isLoadingOutbox = false
      }, failure => {
        console.log('Kunne ikke hente udbakke', failure)
        this.outboxRows = []
        this.isLoadingOutbox = false
        eventbus.toast = {text: 'Kunne ikke hente udbakken', success: false, position: 'bottomLeft'}
      })
    },
    // Bygger rækkerne til Excel-eksport ud fra de aktuelt viste outbox-/afsendte-rækker.
    dataToOutboxExcel () {
      return this.currentOutboxRows.map(row => {
        let createdBy = '—'
        if (row.vCreatedByName) {
          createdBy = row.vCreatedByName
        } else if (row.vCreatedById != null) {
          createdBy = 'Tidligere ansat'
        }
        return {
          created_at: row.vCreatedAt || '',
          user: row.vUserName || '',
          employment: row.vEmploymentLabel || '',
          loenkode: row.vLoenart || '',
          antal: this.formatDecimal(row.antal),
          beloeb: this.formatDecimal(row.beloeb),
          fra: row.vFraDato || '',
          til: row.vTilDato || '',
          operation: this.operationLabel(row.operation) || '',
          created_by: createdBy,
          state: this.stateLabel(row) || ''
        }
      })
    },
    downloadOutboxExcel () {
      if (this.isDownloadingOutbox || this.currentOutboxRows.length === 0) return
      this.isDownloadingOutbox = true
      if (window.XLSX && XLSX) {
        this.writeOutboxExcel()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        const intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.writeOutboxExcel()
          }
        }, 500)
      }
    },
    writeOutboxExcel () {
      const headers = {
        created_at: 'Oprettet',
        user: 'Navn',
        employment: 'Ansættelse',
        loenkode: 'Lønkode',
        antal: 'Enheder',
        beloeb: 'Beløb',
        fra: 'Fra',
        til: 'Til',
        operation: 'Handling',
        created_by: 'Oprettet af',
        state: 'Status'
      }
      const rows = this.dataToOutboxExcel()
      rows.unshift(headers)
      const sheet = XLSX.utils.json_to_sheet(rows, {skipHeader: true})
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, sheet, 'data')
      const label = this.viewTab === 'sent' ? 'afsendte' : 'udbakke'
      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      XLSX.writeFile(wb, `faste_tillaeg_${label}_${timestamp}.xlsx`)
      this.isDownloadingOutbox = false
    },
    formatDecimal (value) {
      if (value == null || value === '') return ''
      const num = Number(value)
      if (isNaN(num)) return String(value).replace('.', ',')
      return num.toFixed(2).replace('.', ',')
    },
    operationLabel (operation) {
      const map = {create: 'Opret', delimit: 'Afgræns', delete: 'Slet'}
      return map[operation] || operation || ''
    },
    stateLabel (row) {
      const map = {new: 'Afventer', sent: 'Sendt', failed: 'Fejlet', read: 'Læst'}
      return map[row.state] || row.state || ''
    },
    statePillClass (row) {
      if (row.state === 'failed') return 'do-pill--red'
      if (row.state === 'sent') return 'do-pill--green'
      return 'do-pill--yellow'
    },
    // Udtrækker beskederne fra OPUS-svaret (result.Meddelelser), hvis de findes.
    resultMessages (row) {
      const result = row.result
      if (!result || typeof result !== 'object') return []
      const list = Array.isArray(result.Meddelelser) ? result.Meddelelser : []
      return list
        .map(m => ({code: m.Kode, type: m.Type, text: m.Tekst || ''}))
        .filter(m => m.text)
    },
    persistFilters (val) {
      try {
        window.localStorage.setItem(this.storageKey, val)
      } catch (e) {
        // ignore storage write errors (e.g. private mode / quota)
      }
    },
    restoreFilters () {
      try {
        const raw = window.localStorage.getItem(this.storageKey)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved) return
        if (saved.viewTab) this.viewTab = saved.viewTab
        if (Array.isArray(saved.selectedNames)) this.selectedNames = saved.selectedNames
        if (Array.isArray(saved.selectedTags)) this.selectedTags = saved.selectedTags
        if (Array.isArray(saved.selectedEmploymentTitles)) this.selectedEmploymentTitles = saved.selectedEmploymentTitles
        if (Array.isArray(saved.selectedOrganisations)) this.selectedOrganisations = saved.selectedOrganisations
      } catch (e) {
        // ignore malformed storage
      }
    }
  },
  watch: {
    filtersSnapshot (val) {
      this.persistFilters(val)
    },
    selectedEmploymentIds (ids) {
      // Forlad bulk-mode automatisk når intet er valgt
      if (!ids || ids.length === 0) {
        this.isBatchMode = false
      }
    }
  },
  created () {
    this.restoreFilters()
  },
  mounted () {
    this.$store.dispatch('readEmployments')
    this.$store.dispatch('readSalaryCodes')
    // Indlæs udbakken med det samme, så antallet vises på fanen ved load.
    this.loadOutbox()
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.faste-tillaeg {
  padding: 1rem;
}

.faste-tillaeg__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.faste-tillaeg__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.faste-tillaeg__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.faste-tillaeg__toolbar__wrap {
  display: inline-flex;
}

.faste-tillaeg__count {
  font-style: italic;
  font-size: 11px;
  color: #6b7280;
}

.faste-tillaeg__outbox-info {
  font-size: 12px;
  line-height: 1.4;
  font-style: italic;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table tbody td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.do-table tbody tr {
  cursor: pointer;
  transition: background-color 0.15s;
}

/* Banding pr. person (tbody-gruppe), ikke pr. række */
.do-table tbody.user-group:nth-of-type(2n) {
  background-color: #f9fafb;
}

/* Zebra-striber pr. række i udbakke-tabellen */
.do-table--striped tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.do-table th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.do-table th.sortable:hover {
  color: var(--primary, #468AD7);
}

.sort-icon {
  font-size: 11px;
  color: inherit;
}

.do-table tbody tr:hover {
  background-color: aliceblue;
}

/* Udbakke-rækker er ikke klikbare som helhed – kun "Slet"-knappen på fejlede linjer. */
.do-table tbody tr.outbox-row {
  cursor: default;
}

.th-actions {
  width: 150px;
}

.td-actions {
  width: 150px;
  text-align: right;
  position: relative;
}

.td-check {
  width: 26px;
}

.do-table thead th.td-check {
  border-right: 1px solid #e2e8f0;
}

.do-table tbody td.td-check {
  vertical-align: middle;
}

.do-table__toggle-all {
  color: var(--primary, #468ad7);
  cursor: pointer;
  user-select: none;
  text-transform: none;
  white-space: nowrap;
}

.do-table__toggle-all:hover {
  text-decoration: underline;
}

.do-table tbody tr.is-selected {
  background-color: #eaf3fb;
}

.do-checkbox {
  border: 1px solid #ccc;
  border-radius: .25rem;
  height: 1rem;
  position: relative;
  width: 1rem;
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
}

.do-checkbox.do-checkbox--selected {
  background: var(--primary);
  border-color: var(--primary);
}

.do-checkbox.do-checkbox--selected:after,
.do-checkbox.do-checkbox--selected:before {
  background: #fff;
}

.do-checkbox:after,
.do-checkbox:before {
  background: transparent;
  transform: rotate(35deg);
}

.do-checkbox:after {
  height: 8px;
  right: 3px;
  top: 1px;
  width: 1.5px;
}

.do-checkbox:after,
.do-checkbox:before {
  border-radius: 1rem;
  content: "";
  display: block;
  position: absolute;
}

.do-checkbox.do-checkbox--selected:before {
  bottom: 2.5px;
  height: 1.5px;
  right: 5px;
  width: 4px;
}

.td-organisation {
  opacity: 0.7;
  font-size: 11px;
}

.faste-tillaeg__outbox-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.td-loenkode,
.do-table thead th.td-loenkode,
.td-loebenr,
.do-table thead th.td-loebenr,
.td-units,
.do-table thead th.td-units {
  text-align: right;
}

.td-loebenr {
  width: 80px;
}

.td-date {
  width: 100px;
}

.td-created {
  width: 110px;
}

.td-units {
  width: 90px;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-status {
  width: 240px;
}

.outbox-msg {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.3;
  color: #6b7280;
}

.outbox-msg--error {
  color: #991b1b;
}

.do-pill--small {
  display: inline-block;
  padding: 0 0.5rem;
  font-size: 10px;
  line-height: 18px;
  border-radius: 10px;
}

.do-pill--yellow {
  background: #fef3c7;
  color: #92400e;
}

.do-pill--green {
  background: #dcfce7;
  color: #166534;
}

.do-pill--red {
  background: #fee2e2;
  color: #991b1b;
}

.do-bttn--small {
  padding: 2px 8px;
  font-size: 11px;
}

.text-link {
  color: var(--primary, #468AD7);
  font-weight: 500;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Knappen placeres absolut, så rækken ikke bliver højere end i bulk-mode */
.row-action-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.12s ease;
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.fast-tillaeg-row:hover .row-action-btn {
  opacity: 1;
  pointer-events: auto;
}

.capitalize {
  text-transform: capitalize;
}

.d-modal__buttons__child {
  padding-right: 1rem;
  display: flex;
  gap: 0.5rem;
}

.do-table tbody td.td-actions {
  width: 150px;
  text-align: right;
}

.beregn-modal {
  width: calc(100vw - 32px);
  height: calc(100vh - 120px);
  background: #f6f9fc;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.beregn-modal__header {
  background: #fff;
  padding: 1rem;
  border-bottom: 1px solid #d8d8d8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.beregn-modal__header h2 {
  margin: 0;
}

.beregn-modal__close {
  cursor: pointer;
  font-size: 1.2rem;
  opacity: 0.85;
}

.beregn-modal__close:hover {
  opacity: 1;
}

.beregn-modal__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1rem;
}

.beregn-modal__footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 1px solid #d8d8d8;
}

.send-to-opus-wrap {
  display: inline-flex;
}

.beregn-menu {
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 340px;
}

.beregn-menu li {
  white-space: normal;
  line-height: 1.3;
}
</style>



// WEBPACK FOOTER //
// FasteTillaeg.vue?7df544ca